import { useEffect, useState, useRef, useMemo } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { BriefButton } from "@/components/drafting/BriefButton"
import { BriefModal } from "@/components/drafting/BriefModal"
import { useCoverageStore } from "@/stores/coverage-store"
import { Button } from "@/components/ui/button"
import { OutlineProposalArea } from "@/components/outline-proposal/OutlineProposalArea"
import { ProposeMoreSectionsButton } from "@/components/outline-proposal/ProposeMoreSectionsButton"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Plus,
  Loader2,
  AlertCircle,
  GripVertical,
  MoreHorizontal,
  FileEdit,
  Check,
  Unlock,
  LayoutTemplate,
  ListTree,
  BookOpen,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useResponsesStore } from "@/stores/responses-store"
import { useDraftingStore } from "@/stores/drafting-store"
import { useSectionAssignmentsStore, type CollaborationStatus } from "@/stores/section-assignments-store"
import { useAuthStore } from "@/stores/auth-store"
import { useBidReadOnly, useIsViewOnly } from "@/hooks/useViewOnly"
import { ViewOnlyNotice } from "@/components/ViewOnlyNotice"
import { useOutlineTemplatesStore } from "@/stores/outline-templates-store"
import { exportArtifactToDocx, api, getArtifactSubmissionLinks, toastApiError } from "@/lib/api"
import { useSharePointGate } from "@/hooks/useSharePointGate"
import { useExportGapCheck } from "@/hooks/useExportGapCheck"
import { ExportGapConfirmDialog } from "@/components/drafting/ExportGapConfirmDialog"
import { SharePointBrowser } from "@/components/SharePointBrowser"
import type { OutlineSection, SectionStatus, ArtifactSubmissionLink, OutlineTemplateSection, ArtifactPendingSuggestion, ExportOptions } from "@/lib/api"
import { routeTemplateExportError } from "@/stores/export-templates-store"
import { usePdfExportStore } from "@/stores/pdf-export-store"
import { TemplateFormDialog } from "@/components/templates/TemplateFormDialog"
import { TemplatePickerDialog } from "@/components/templates/TemplatePickerDialog"
import { toast } from "sonner"
import { toastExportResult } from '@/lib/pending-ai-change-notices'
import { collaborationStatusConfig, summariseSectionProgress, tableWordCount, type CollaborationStatusKey, COLLABORATION_STATUSES, PENDING_SUGGESTION_STATUS } from "@/lib/section-status"
import { cn } from "@/lib/utils"
import { SubmissionLinksIndicator } from "@/components/extraction/SubmissionLinksIndicator"
import { type BidMemberInfo, ArtifactOwnerSelector, SectionOwnerCell, SectionReviewerCell } from "@/components/ui/owner-assignment"
import { SubmitForReviewButton } from "@/components/SubmitForReviewButton"
import { ReviewStatusBanner } from "@/components/ReviewStatusBanner"
import { ArtifactStatusBadge } from "@/components/ArtifactStatusBadge"
import { ArtifactPageHeader } from "@/components/ArtifactPageHeader"
import { SingleDocumentOverviewPage } from "@/components/SingleDocumentOverviewPage"
import { ExportDropdown } from "@/components/ExportDropdown"
import { DraftAllButton, DraftAllNotice, DraftDocumentButton } from "@/components/drafting/DraftAllSections"
import { PanelDocumentJob } from "@/components/drafting/PanelDocumentJob"
import { PendingSuggestionsList } from "@/components/drafting/PendingSuggestionsList"
import { DocumentQuestionsList } from "@/components/DocumentQuestionsList"
import { useActiveJobsStore } from "@/stores/active-jobs-store"
import { hasDraftedRecap } from "@/lib/document-job-progress"
import { useDraftAll } from "@/hooks/useDraftAll"
import { useDraftDocument } from "@/hooks/useDraftDocument"
import { SECTIONS_TABLE_COLUMNS, UPDATED_CELL_VISIBILITY } from "@/lib/sections-table-columns"

// ===================
// Helper Functions
// ===================

const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

/**
 * "24 Sep" — the table's Updated column; the time lives in the tooltip.
 * Built by hand: en-AU's `month: "short"` gives "Sept", and the column wants a
 * steady three letters.
 */
function formatShortDate(dateStr: string | null): string {
  if (!dateStr) return "—"
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return "—"
  return `${date.getDate()} ${SHORT_MONTHS[date.getMonth()]}`
}

/** "14 Aug 2026, 3:05 pm" — tooltip for the Updated column. */
function formatFullDate(dateStr: string | null): string | undefined {
  if (!dateStr) return undefined
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return undefined
  return date.toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}


function generateSectionKey(): string {
  return `section-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

// ===================
// Progress Bar
// ===================

interface ProgressSummaryProps {
  sections: OutlineSection[]
  assignments: { sectionId: string; collaborationStatus: string }[]
  sectionStatuses: SectionStatus[]
  /** Right-hand actions in the same flat row, left of "% drafted". */
  action?: React.ReactNode
}

function ProgressSummary({ sections, assignments, sectionStatuses, action }: ProgressSummaryProps) {
  // Word counts matter as well as status: AI drafting only moves a section to
  // In progress, so a fully written section would otherwise read "to write".
  const { total, counts, draftedPercent } = summariseSectionProgress(
    sections.map((s) => ({
      status: assignments.find((a) => a.sectionId === s.sectionKey)?.collaborationStatus ?? 'not_started',
      wordCount: sectionStatuses.find((st) => st.sectionKey === s.sectionKey)?.wordCount ?? null,
    })),
  )

  // One summary line, work first: sections · in progress · still to write ·
  // approved. Complete and awaiting-approval only appear when non-zero.
  // Drafted, not approved, on the right — approval readiness is stated in words
  // beside the Submit-for-review button.
  const parts = [
    `${total} section${total !== 1 ? "s" : ""}`,
    `${counts.inProgress} in progress`,
    `${counts.toWrite} still to write`,
    ...(counts.awaitingApproval > 0 ? [`${counts.awaitingApproval} awaiting approval`] : []),
    ...(counts.complete > 0 ? [`${counts.complete} complete`] : []),
    `${counts.approved} approved`,
  ]
  return (
    <div className="px-1 py-2 mb-1 flex items-center justify-between gap-4" data-testid="section-progress-row">
      <p className="text-meta" data-testid="section-progress-summary">{parts.join(" · ")}</p>
      <div className="flex items-center gap-3 shrink-0">
        {action}
        <span className="text-xs font-medium">{draftedPercent}% drafted</span>
      </div>
    </div>
  )
}

// ===================
// Words against target
// ===================

/**
 * Columns every viewport shows. The Add-section row spans only these: a
 * colSpan wider than the rendered columns would create an extra implicit
 * column in the fixed layout and squeeze Section below 2xl.
 */
const ALWAYS_VISIBLE_COLUMN_COUNT = SECTIONS_TABLE_COLUMNS.filter((c) => !c.wideOnly).length

interface WordsAgainstTargetProps {
  wordCount: number | null
  target: number | null
  isEditing: boolean
  inputRef: React.RefObject<HTMLInputElement>
  value: string
  onValueChange: (value: string) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  onSave: () => void
  onStartEdit: () => void
  isFinalized: boolean
  /** Full "updated" date, folded into the tooltip while the Updated column is hidden (below 2xl). */
  updatedLabel?: string | undefined
}

/**
 * `593 / 650` with a 28px bar beside it (amber once over target); the bar
 * wraps under the numbers only when they are too wide to share the line. The
 * target half is the click-to-edit control; words come from the draft.
 */
function WordsAgainstTarget({
  wordCount,
  target,
  isEditing,
  inputRef,
  value,
  onValueChange,
  onKeyDown,
  onSave,
  onStartEdit,
  isFinalized,
  updatedLabel,
}: WordsAgainstTargetProps) {
  const words = wordCount != null ? wordCount.toLocaleString() : "—"
  const hasTarget = target != null && target > 0
  const isOver = hasTarget && wordCount != null && wordCount > target
  const fillPercent = hasTarget && wordCount != null ? Math.min(100, Math.round((wordCount / target) * 100)) : 0
  const tooltip = [
    hasTarget ? `${words} / ${target.toLocaleString()} words` : `${words} words`,
    ...(updatedLabel ? [`updated ${updatedLabel}`] : []),
  ].join(" · ")

  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1" title={tooltip} data-testid="words-cell">
      <div className="flex items-center gap-0.5 text-sm tabular-nums whitespace-nowrap">
        <span className={cn(wordCount == null && "text-muted-foreground")}>{words}</span>
        <span className="text-muted-foreground">/</span>
        {isEditing ? (
          <Input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            aria-label="Target word count"
            value={value}
            onChange={(e) => onValueChange(e.target.value.replace(/\D/g, ""))}
            onKeyDown={onKeyDown}
            onBlur={onSave}
            className="h-6 w-14 px-1 text-sm"
          />
        ) : (
          <button
            type="button"
            className={cn(
              "text-muted-foreground rounded-sm",
              !isFinalized && "hover:text-foreground hover:underline cursor-pointer"
            )}
            onClick={onStartEdit}
            disabled={isFinalized}
            title={isFinalized ? undefined : "Click to set target word count"}
          >
            {hasTarget ? target.toLocaleString() : "—"}
          </button>
        )}
      </div>
      {hasTarget && (
        <div className="h-1 w-7 shrink-0 rounded-full bg-muted overflow-hidden" aria-hidden="true">
          <div
            data-testid="words-bar"
            data-over={isOver ? "true" : "false"}
            className={cn(
              "h-full rounded-full",
              isOver ? "bg-[hsl(var(--status-warning))]" : "bg-muted-foreground"
            )}
            style={{ width: `${fillPercent}%` }}
          />
        </div>
      )}
    </div>
  )
}

// ===================
// Section Row
// ===================

interface SectionRowProps {
  section: OutlineSection
  index: number
  status: SectionStatus | undefined
  collaborationStatus: CollaborationStatusKey | null
  /** An AI suggestion waiting on this section: the table says "Waiting on your review" (display only). */
  pendingSuggestion?: ArtifactPendingSuggestion | null
  isEditing: boolean
  editValue: string
  onEditChange: (value: string) => void
  onEditStart: () => void
  onEditSave: () => void
  onEditCancel: () => void
  onDelete: () => void
  onStartDrafting: () => void
  onStatusChange: (status: string) => void
  onUpdateTargetWordCount: (value: number | undefined) => void
  isFinalized: boolean
  totalSections: number
  dragIndex: number | null
  dropTargetIndex: number | null
  onDragStart: (index: number) => void
  onDragOver: (e: React.DragEvent, index: number) => void
  onDrop: () => void
  onDragEnd: () => void
  submissionLinks?: React.ReactNode
  ownerCell?: React.ReactNode
  reviewerCell?: React.ReactNode
  briefButton?: React.ReactNode
}

function SectionRow({
  section,
  index,
  status,
  collaborationStatus,
  pendingSuggestion = null,
  isEditing,
  editValue,
  onEditChange,
  onEditStart,
  onEditSave,
  onEditCancel,
  onDelete,
  onStartDrafting,
  onStatusChange,
  onUpdateTargetWordCount,
  isFinalized,
  totalSections,
  dragIndex,
  dropTargetIndex,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  submissionLinks,
  ownerCell,
  reviewerCell,
  briefButton,
}: SectionRowProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const targetInputRef = useRef<HTMLInputElement>(null)
  const canDragRef = useRef(false)
  const [isEditingTarget, setIsEditingTarget] = useState(false)
  const [targetValue, setTargetValue] = useState('')
  const statusKey: CollaborationStatusKey = collaborationStatus ?? 'not_started'
  const statusInfo = collaborationStatusConfig[statusKey] ?? collaborationStatusConfig['not_started']
  // A waiting AI suggestion is what the section needs next: say so in place of its status.
  // Display only — the status itself, and the menu that changes it, are untouched.
  const pill = pendingSuggestion ? PENDING_SUGGESTION_STATUS : statusInfo

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  useEffect(() => {
    if (isEditingTarget && targetInputRef.current) {
      targetInputRef.current.focus()
      targetInputRef.current.select()
    }
  }, [isEditingTarget])

  const handleTargetClick = () => {
    if (isFinalized) return
    setTargetValue(section.targetWordCount?.toString() ?? '')
    setIsEditingTarget(true)
  }

  const handleTargetSave = () => {
    const parsed = parseInt(targetValue, 10)
    onUpdateTargetWordCount(parsed > 0 ? parsed : undefined)
    setIsEditingTarget(false)
  }

  const handleTargetKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleTargetSave()
    else if (e.key === 'Escape') setIsEditingTarget(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      onEditSave()
    } else if (e.key === "Escape") {
      onEditCancel()
    }
  }

  return (
    <tr
      className={cn(
        "group border-b hover:bg-muted/50 transition-colors",
        dropTargetIndex === index && dragIndex !== null && "border-t-2 border-t-primary",
        dropTargetIndex === index + 1 && index === totalSections - 1 && dragIndex !== null && "border-b-2 border-b-primary"
      )}
      draggable={!isFinalized}
      onDragStart={(e) => {
        if (!canDragRef.current || isFinalized) {
          e.preventDefault()
          return
        }
        e.dataTransfer.effectAllowed = 'move'
        onDragStart(index)
      }}
      onDragOver={(e) => {
        if (isFinalized || dragIndex === null) return
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        onDragOver(e, index)
      }}
      onDrop={(e) => {
        if (isFinalized) return
        e.preventDefault()
        onDrop()
      }}
      onDragEnd={() => {
        canDragRef.current = false
        onDragEnd()
      }}
    >
      {/* Drag Handle */}
      <td className="py-2 text-center">
        {!isFinalized && (
          <div
            className="flex items-center justify-center cursor-grab active:cursor-grabbing"
            onMouseDown={() => { canDragRef.current = true }}
            onMouseUp={() => { canDragRef.current = false }}
          >
            <GripVertical className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
          </div>
        )}
      </td>

      {/* Section Number */}
      <td className="pl-1 pr-1 py-2">
        <span className="text-meta">{index + 1}</span>
      </td>

      {/* Title — gets the width; long names wrap to two lines at most */}
      <td className="px-3 py-2">
        {isEditing ? (
          <Input
            ref={inputRef}
            value={editValue}
            onChange={(e) => onEditChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={onEditSave}
            className="h-8 text-sm"
          />
        ) : (
          <button
            className="text-sm text-left line-clamp-2 break-words hover:text-[hsl(var(--brand))] transition-colors"
            onClick={onStartDrafting}
            onDoubleClick={(e) => {
              e.stopPropagation()
              onEditStart()
            }}
            title={isFinalized ? section.title : `${section.title} — click to draft, double-click to rename`}
          >
            {section.title}
          </button>
        )}
      </td>

      {/* Status — key forces DOM re-creation when status changes via store refetch.
          Tight padding (cell and pill) so "Waiting on your review" fits in 172px. */}
      <td className="pl-1.5 pr-1 py-2">
        {isFinalized ? (
          <Badge variant={pill?.variant ?? "secondary"} dot className="px-1.5 text-xs whitespace-nowrap">
            {pill?.label ?? collaborationStatusConfig.not_started.label}
          </Badge>
        ) : (
        <Popover key={statusKey}>
          <PopoverTrigger asChild>
            <button className="cursor-pointer">
              <Badge variant={pill?.variant ?? "secondary"} dot className="px-1.5 text-xs whitespace-nowrap hover:opacity-80 transition-opacity">
                {pill?.label ?? collaborationStatusConfig.not_started.label}
              </Badge>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-44 p-1" align="start">
            {COLLABORATION_STATUSES.map((s) => (
              <button
                key={s.key}
                onClick={() => onStatusChange(s.key)}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-muted transition-colors"
              >
                <div className={cn('h-2 w-2 rounded-full', s.dotColor)} />
                <span className="flex-1 text-left">{s.label}</span>
                {statusKey === s.key && <Check className="h-3.5 w-3.5 text-muted-foreground" />}
              </button>
            ))}
          </PopoverContent>
        </Popover>
        )}
      </td>

      {/* Owner */}
      <td className="px-2 py-2">
        {ownerCell}
      </td>

      {/* Reviewer — requested reviewer, or the planned one */}
      <td className="px-2 py-2">
        {reviewerCell}
      </td>

      {/* Submission — this section's linked item(s) */}
      <td className="px-2 py-2">
        {submissionLinks}
      </td>

      {/* Words / target — the target stays click-to-edit. The wide left
          padding is the gap from Submission; 116px fits "661 / 650"
          and the 28px bar on one line. */}
      <td className="pl-3 pr-1 py-2">
        <WordsAgainstTarget
          wordCount={tableWordCount(status?.wordCount, pendingSuggestion)}
          target={section.targetWordCount ?? null}
          isEditing={isEditingTarget}
          inputRef={targetInputRef}
          value={targetValue}
          onValueChange={setTargetValue}
          onKeyDown={handleTargetKeyDown}
          onSave={handleTargetSave}
          onStartEdit={handleTargetClick}
          isFinalized={isFinalized}
          updatedLabel={formatFullDate(status?.updatedAt ?? null)}
        />
      </td>

      {/* Brief — always visible: set vs unset shows at a glance */}
      <td className="px-1 py-2">
        {briefButton}
      </td>

      {/* Last Updated — only from 2xl; below that the date is in the Words tooltip */}
      <td className={cn(UPDATED_CELL_VISIBILITY, "px-2 py-2 text-meta whitespace-nowrap")}>
        <span title={formatFullDate(status?.updatedAt ?? null)}>
          {formatShortDate(status?.updatedAt ?? null)}
        </span>
      </td>

      {/* Actions — ⋯ only, pinned to the right edge so it is reachable at any
          width (the table scrolls sideways on narrow screens). The cell is
          opaque (card + the row's hover wash) so content scrolling under it is
          hidden. */}
      <td className="sticky right-0 z-[1] bg-card px-1 py-2">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 group-hover:bg-muted/50 transition-colors" />
        <div className="relative flex items-center justify-end">
          {!isFinalized && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Actions for ${section.title}`}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEditStart}>Rename</DropdownMenuItem>
              <DropdownMenuItem
                onClick={onDelete}
                className="text-destructive focus:text-destructive"
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>
      </td>
    </tr>
  )
}

// ===================
// Add Section Row
// ===================

interface AddSectionRowProps {
  onAdd: () => void
}

function AddSectionRow({ onAdd }: AddSectionRowProps) {
  return (
    <tr>
      <td colSpan={ALWAYS_VISIBLE_COLUMN_COUNT} className="px-3 py-2">
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-foreground"
          onClick={onAdd}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add section
        </Button>
      </td>
    </tr>
  )
}

// ===================
// Main Component
// ===================

export function ArtifactDetailPage() {
  const { bidId, artifactId } = useParams<{ bidId: string; artifactId: string }>()
  const navigate = useNavigate()
  const { search } = useLocation()

  // Store state
  const {
    selectedArtifact: artifact,
    selectedArtifactPendingSuggestions,
    isLoading: artifactLoading,
    error: artifactError,
    fetchArtifactById,
    refreshPendingSuggestions,
    clearError: clearArtifactError,
    updateArtifact,
    reopenArtifact,
  } = useResponsesStore()

  const {
    outline,
    outlineVersion,
    sectionStatuses,
    isLoadingOutline,
    isSavingOutline,
    error: outlineError,
    fetchOutline,
    fetchSectionStatuses,
    saveOutline,
    addSection,
    updateSection,
    removeSection,
    reorderSections,
    clearError: clearOutlineError,
  } = useDraftingStore()

  const { assignments, fetchAssignments, assignSection, unassignSection, updateCollaborationStatus, planReviewer } = useSectionAssignmentsStore()
  const currentUserId = useAuthStore((s) => s.user?.id)
  // Org admins pass the server's requireBidRole check on owner assignment and
  // planned reviewers, so the table offers them the same controls.
  const isOrgAdmin = useAuthStore((s) => s.role === 'admin')
  // A read-only user — an org viewer, or a member who is not on the bid —
  // reads the outline and opens sections; every change is refused them.
  const { isReadOnly } = useBidReadOnly(bidId)
  // Exporting and saving the outline as a template are refused an org viewer
  // only: a member who is not on the bid may still do both.
  const isViewOnly = useIsViewOnly()

  // Bid members for owner check and assignment popovers
  const [bidMembers, setBidMembers] = useState<BidMemberInfo[]>([])
  const isBidOwner = bidMembers.some((m) => m.userId === currentUserId && m.role === 'owner')
  const isArtifactOwner = artifact?.ownerId === currentUserId
  const isOwner = isBidOwner  // backwards-compat alias for finalize/reopen checks
  // Mirrors the server rule for POST .../assign (owner and planned reviewer):
  // document owner, bid owner, or org admin.
  const canAssignSections = !isReadOnly && (isBidOwner || isArtifactOwner || isOrgAdmin)

  // Status-derived flags
  const isFinalized = artifact?.status === 'finalized'
  const isInReview = artifact?.status === 'pending_review'
  const isFrozen = isFinalized || isInReview
  // The outline can't change: frozen for everyone, or a read-only reader.
  const isLocked = isFrozen || isReadOnly
  const [showReopenDialog, setShowReopenDialog] = useState(false)
  const [isReopening, setIsReopening] = useState(false)

  // SharePoint save dialog
  const [sharePointSaveOpen, setSharePointSaveOpen] = useState(false)
  const [sharePointExportTemplateId, setSharePointExportTemplateId] = useState<string | null>(null)
  // Only the connection flag + probe: this page's disconnected branch routes to
  // Integrations settings rather than starting an OAuth connect, so it has no
  // `openOrConnect` gate to hand off.
  const { isConnected: isSharePointConnected, ensureChecked: ensureSharePointChecked } =
    useSharePointGate()
  useEffect(() => {
    ensureSharePointChecked()
  }, [ensureSharePointChecked])

  // Local state
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null)
  const [editingSectionKey, setEditingSectionKey] = useState<string | null>(null)
  const [editValue, setEditValue] = useState("")
  const [deleteTarget, setDeleteTarget] = useState<OutlineSection | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  const [submissionLinks, setSubmissionLinks] = useState<ArtifactSubmissionLink[]>([])

  // Brief modal — opened from a section row, before entering the editor.
  const [briefSection, setBriefSection] = useState<{ sectionKey: string; title: string } | null>(null)
  const [briefOpen, setBriefOpen] = useState(false)
  const loadCoverage = useCoverageStore((s) => s.load)

  // Template state
  const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false)
  const [showTemplatePicker, setShowTemplatePicker] = useState(false)
  const [showTemplateReplaceConfirm, setShowTemplateReplaceConfirm] = useState(false)
  const [pendingTemplateSections, setPendingTemplateSections] = useState<OutlineTemplateSection[] | null>(null)
  const { createTemplate } = useOutlineTemplatesStore()

  // Fetch data on mount
  useEffect(() => {
    if (bidId) void loadCoverage(bidId)
    if (artifactId) {
      void fetchArtifactById(artifactId)
      void fetchOutline(artifactId)
      void fetchSectionStatuses(artifactId)
      void fetchAssignments(artifactId)
      void getArtifactSubmissionLinks(artifactId)
        .then((data) => setSubmissionLinks(data.links))
        .catch(() => setSubmissionLinks([]))
    }
  }, [artifactId, bidId, loadCoverage, fetchArtifactById, fetchOutline, fetchSectionStatuses, fetchAssignments])

  // Redirect excel_schedule and word_form artifacts to their fill-session
  // pages. Same matched-id guard as the questionnaire bounce below.
  useEffect(() => {
    if (artifact?.id !== artifactId || !bidId) return
    const page =
      artifact?.artifactType === 'excel_schedule'
        ? 'schedule'
        : artifact?.artifactType === 'word_form'
          ? 'form'
          : null
    if (page) {
      void navigate(`/app/bids/${bidId}/responses/${artifactId}/${page}${search}`, {
        replace: true,
      })
    }
  }, [artifact?.id, artifact?.artifactType, bidId, artifactId, navigate, search])

  // Redirect questionnaire artifacts to questionnaire overview page
  // Only redirect when the loaded artifact matches the current URL to avoid
  // stale redirects when switching between artifact types
  useEffect(() => {
    if (artifact?.id === artifactId && artifact?.artifactType === 'questionnaire' && bidId) {
      // Carry the query string across — a `?commentId=` deep link that lands
      // here must survive the bounce to the questionnaire route.
      void navigate(`/app/bids/${bidId}/responses/${artifactId}/questionnaire${search}`, {
        replace: true,
      })
    }
  }, [artifact?.id, artifact?.artifactType, bidId, artifactId, navigate, search])

  // Fetch bid members for owner check and assignment popovers
  useEffect(() => {
    if (bidId) {
      void api.get<{ members: BidMemberInfo[] }>(`/bids/${bidId}/members`)
        .then((data) => setBidMembers(data.members))
        .catch(() => { /* non-critical */ })
    }
  }, [bidId])

  // "Draft all sections" — multi-section response documents only. Followed even
  // while the document is frozen, so a run already going still reports back.
  const isMultiSectionDocument =
    artifact?.id === artifactId &&
    artifact?.scopeKind === 'unit' &&
    artifact?.artifactType === 'narrative_document'
  const draftAll = useDraftAll(artifactId, {
    enabled: isMultiSectionDocument,
    // The server's outline version, not the local section list: edits here are
    // optimistic, and readiness is read from the SAVED outline. Keying on the
    // local list re-read it before the save landed and missed a new section.
    outlineSignature: String(outlineVersion),
    onSettled: () => {
      if (!artifactId) return
      void fetchSectionStatuses(artifactId)
      void fetchAssignments(artifactId)
    },
  })

  // "Draft document" (R1): replaces Draft all sections while the server's
  // whole-document job is switched on. While the switch is first being read,
  // neither button shows (the old one used to flash up, clickable, for a
  // moment on every load). Once the server says off, or the read fails, the
  // page keeps Draft all sections exactly as before.
  const draftDocument = useDraftDocument(artifactId, {
    enabled: isMultiSectionDocument,
    // Same key as Draft all: the SAVED outline's version (UX2 01).
    outlineSignature: String(outlineVersion),
    // The job saved section text: re-read what the sections table shows (word
    // counts, % drafted). Re-reads only — section status is left alone.
    // It may also have held suggestions back on sections with text: re-read
    // the "suggestions waiting" list, and only that (not the artifact or the
    // outline, which the user may be editing).
    onJobFinished: () => {
      if (!artifactId) return
      void fetchSectionStatuses(artifactId)
      void fetchAssignments(artifactId)
      void refreshPendingSuggestions(artifactId)
    },
  })
  const showDraftDocument = draftDocument.available === true

  // Why Submit-for-review is blocked, or null when it's allowed. Counts what is
  // outstanding rather than restating the rule — "5 of 6 sections still need
  // approval" tells you where you are; "all sections must be approved" doesn't.
  const submitDisabledReason = useMemo(() => {
    const sections = outline?.sections ?? []
    if (sections.length === 0) return 'Add sections before submitting'
    const outstanding = sections.filter((s) => {
      const assignment = assignments.find((a) => a.sectionId === s.sectionKey)
      const status = assignment?.collaborationStatus ?? 'not_started'
      return status !== 'approved' && status !== 'complete'
    }).length
    if (outstanding === 0) return null
    const noun = sections.length === 1 ? 'section' : 'sections'
    const verb = outstanding === 1 ? 'needs' : 'need'
    return `${outstanding} of ${sections.length} ${noun} still ${verb} approval`
  }, [outline?.sections, assignments])

  // Handlers
  const handleSetArtifactOwner = async (userId: string | null) => {
    if (!artifact?.id) return
    try {
      await updateArtifact(artifact.id, { ownerId: userId })
      toast.success(userId ? "Document owner updated" : "Document owner removed")
    } catch {
      toast.error("Failed to update document owner")
    }
  }

  // Persist the current outline (post-mutation) to the server. Read from the store
  // so callers don't have to pass stale snapshots. Section statuses are re-read
  // once the save lands so a new row picks up its Updated time.
  const autoSaveOutline = () => {
    if (!artifactId) return
    const current = useDraftingStore.getState().outline
    if (!current) return
    void saveOutline(artifactId, current).then(() => fetchSectionStatuses(artifactId))
  }

  const handleAddSectionWithEdit = () => {
    const sectionKey = generateSectionKey()
    const title = 'Untitled Section'
    addSection({ sectionKey, title })
    setEditingSectionKey(sectionKey)
    setEditValue(title)
    autoSaveOutline()
  }

  const handleDrop = () => {
    if (dragIndex === null || dropTargetIndex === null) {
      setDragIndex(null)
      setDropTargetIndex(null)
      return
    }
    // dropTargetIndex is an insertion point. After splice-removing the dragged
    // item, indices shift down for items after it.
    const toIndex = dropTargetIndex > dragIndex ? dropTargetIndex - 1 : dropTargetIndex
    if (toIndex !== dragIndex) {
      reorderSections(dragIndex, toIndex)
      autoSaveOutline()
    }
    setDragIndex(null)
    setDropTargetIndex(null)
  }

  const handleEditStart = (section: OutlineSection) => {
    setEditingSectionKey(section.sectionKey)
    setEditValue(section.title)
  }

  const handleEditSave = () => {
    if (editingSectionKey && editValue.trim()) {
      updateSection(editingSectionKey, { title: editValue.trim() })
      autoSaveOutline()
    }
    setEditingSectionKey(null)
    setEditValue("")
  }

  const handleEditCancel = () => {
    setEditingSectionKey(null)
    setEditValue("")
  }

  const handleDelete = () => {
    if (deleteTarget) {
      removeSection(deleteTarget.sectionKey)
      setDeleteTarget(null)
      autoSaveOutline()
    }
  }

  const handleStartDrafting = (sectionKey: string) => {
    void navigate(`/app/bids/${bidId}/responses/${artifactId}/sections/${sectionKey}`)
  }

  // R1 W2: staged suggestions waiting on this document (null until read). Opening
  // one goes to its section's editor, whose session load brings the review strip up.
  const pendingSuggestionItems = selectedArtifactPendingSuggestions && selectedArtifactPendingSuggestions.artifactId === artifactId
    ? selectedArtifactPendingSuggestions.items
    : null
  const waitingSuggestions = (pendingSuggestionItems ?? []).map((item) => ({
    proposalId: item.proposalId,
    sectionId: item.sectionKey,
    heading: item.heading,
  }))
  const pendingBySection = useMemo(
    () => new Map((pendingSuggestionItems ?? []).map((item) => [item.sectionKey, item])),
    [pendingSuggestionItems]
  )
  // The finished job's recap lists the waiting suggestions itself (draft page
  // redesign), so the separate list would say the same thing twice.
  const recapListsSuggestions = useActiveJobsStore((s) => {
    const job = artifactId ? s.getOverviewDocumentJob(artifactId) : null
    return job ? hasDraftedRecap(job) : false
  })

  const { runWithGapCheck, exportGapDialogProps } = useExportGapCheck({
    artifactId,
    outlineSections: outline?.sections ?? [],
    fallbackTitle: artifact?.name ?? 'This document',
    onReviewGaps: (sectionKey) => {
      if (sectionKey) handleStartDrafting(sectionKey)
    },
  })

  const handleReopen = async () => {
    if (!artifactId) return
    try {
      setIsReopening(true)
      await reopenArtifact(artifactId)
      setShowReopenDialog(false)
      toast.success('Artifact reopened')
    } catch {
      toast.error('Failed to reopen artifact')
    } finally {
      setIsReopening(false)
    }
  }

  const handleSaveAsTemplate = async (data: {
    name: string
    description?: string
    sections: OutlineTemplateSection[]
    visibility: 'private' | 'shared'
  }) => {
    await createTemplate(data)
    toast.success('Template saved')
  }

  const handleApplyTemplate = (templateSections: OutlineTemplateSection[]) => {
    if (sections.length > 0) {
      setPendingTemplateSections(templateSections)
      setShowTemplateReplaceConfirm(true)
    } else {
      applyTemplateSections(templateSections)
    }
  }

  const applyTemplateSections = (templateSections: OutlineTemplateSection[]) => {
    // Clear existing sections and add template sections
    for (const s of [...sections]) {
      removeSection(s.sectionKey)
    }
    for (const ts of templateSections) {
      const newSection: OutlineSection = {
        sectionKey: generateSectionKey(),
        title: ts.title,
        status: 'not_started',
      }
      if (ts.description) newSection.description = ts.description
      if (ts.targetWordCount) newSection.targetWordCount = ts.targetWordCount
      addSection(newSection)
    }
    autoSaveOutline()
    toast.success('Template applied')
  }

  const runExport = async (templateId?: string, options?: ExportOptions) => {
    if (!artifactId) return
    setIsExporting(true)
    try {
      // R3: a PDF is converted from the export's exact Word file; the export
      // menu shows its progress and result.
      const isPdf = options?.format === 'pdf'
      const { pendingAiChanges } = isPdf
        ? await usePdfExportStore.getState().exportPdf(artifactId, templateId, options)
        : await exportArtifactToDocx(artifactId, undefined, templateId, options)
      toastExportResult(isPdf ? 'PDF exported successfully' : 'Document exported successfully', pendingAiChanges)
    } catch (err) {
      // A template that would replace its own pages asks first (D4) — for a
      // PDF exactly as for Word, so the retry keeps the format.
      const retry = () => void runExport(templateId, { ...options, acknowledgeTemplateLoss: true })
      if (!routeTemplateExportError(err, retry)) toastApiError(err, 'Export failed. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  // Spec 19: hold the export while the draft still shows evidence gaps, so the
  // strip is never silent. `runWithGapCheck` runs the export itself when the
  // draft is clean, or when the report call fails.
  const handleExport = async (templateId?: string, options?: ExportOptions) => {
    if (!artifactId) return
    setIsExporting(true)
    try {
      await runWithGapCheck(() => runExport(templateId, options))
    } finally {
      setIsExporting(false)
    }
  }

  // SharePoint is a second export route out of the same dropdown, reading the
  // same draft through the same renderer — so it strips the same annotations and
  // has to clear the same gate. Gating only the .docx item would leave the
  // silent strip intact one menu row down.
  //
  // `isExporting` is held across the report exactly as the .docx route holds it,
  // and for a second reason: both routes share ONE `pending` stash. Left live,
  // a .docx click while this report was in flight would overwrite the stash, and
  // "Export anyway" on a dialog opened from SharePoint would download a .docx.
  const handleSaveToSharePoint = async (templateId?: string) => {
    if (!artifactId) return
    setIsExporting(true)
    try {
      await runWithGapCheck(() => {
        setSharePointExportTemplateId(templateId ?? null)
        setSharePointSaveOpen(true)
      })
    } finally {
      setIsExporting(false)
    }
  }

  // A quiet plan: no notification, no review round, no status change.
  // "Request review" in the editor is what notifies the reviewer.
  const handlePlanReviewer = async (sectionKey: string, userId: string | null) => {
    if (!artifactId) return
    try {
      await planReviewer(artifactId, sectionKey, userId)
    } catch (err) {
      toastApiError(err, userId ? 'Failed to plan the reviewer' : 'Failed to remove the planned reviewer')
    }
  }

  const handleStatusChange = async (sectionKey: string, statusValue: string) => {
    if (!artifactId) return
    try {
      await updateCollaborationStatus(artifactId, sectionKey, statusValue as CollaborationStatus)
    } catch (err) {
      toastApiError(err, 'Failed to update section status')
    }
  }

  // Loading state
  const isLoading = artifactLoading || isLoadingOutline
  const error = artifactError || outlineError

  if (isLoading && !artifact) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button
          variant="outline"
          onClick={() => {
            clearArtifactError()
            clearOutlineError()
          }}
        >
          Try Again
        </Button>
      </div>
    )
  }

  // Single-document artifacts get their own overview page
  if (artifact?.id === artifactId && artifact?.scopeKind === 'artifact') {
    return <SingleDocumentOverviewPage />
  }

  // Questionnaires and Excel schedules are bounced to their own routes by the
  // effects above, but only AFTER this render commits. Rendering the prose
  // page meanwhile fed a questionnaire's questions (`prompt`, no `title`) to
  // the narrative outline components and crashed the route (2026-09-27).
  const isRedirectingAway =
    artifact?.id === artifactId &&
    (artifact?.artifactType === 'questionnaire' ||
      artifact?.artifactType === 'excel_schedule' ||
      artifact?.artifactType === 'word_form')
  if (isRedirectingAway) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // The outline proposal area is prose-only. Until THIS artifact has loaded,
  // the store may still hold the previous one, so its type proves nothing.
  const isNarrativeArtifact =
    artifact?.id === artifactId && artifact?.artifactType === 'narrative_document'

  const sections = outline?.sections ?? []
  // Where each question's answer goes, named in the question list.
  const sectionHeadings = Object.fromEntries(sections.map((section) => [section.sectionKey, section.title]))
  // One filled button per screen: Start drafting steps down to secondary only
  // while Draft document renders beside it (same condition as the header).
  const isDraftDocumentInHeader =
    isMultiSectionDocument && !isLocked && sections.length > 0 && showDraftDocument

  // Shared by the first-run and additions mounts of OutlineProposalArea, which
  // keeps them on screen for the whole proposal review rather than dropping
  // them the moment a proposal completes.
  //
  // Template is a first-run-only path: applying one CLEARS every existing
  // section (`applyTemplateSections`), so on a document that already has an
  // outline "Start from Template" is a destructive replace wearing a
  // create-shaped label. The overflow menu already makes the same distinction
  // (sections → Save as Template; none → From Template), and the replace route
  // stays reachable from there.
  const outlineManualActions = (
    <>
      <Button variant="ghost" onClick={handleAddSectionWithEdit}>
        <Plus className="h-4 w-4 mr-2" />
        Add Section
      </Button>
      {sections.length === 0 && (
        <Button variant="ghost" onClick={() => setShowTemplatePicker(true)}>
          <LayoutTemplate className="h-4 w-4 mr-2" />
          Start from Template
        </Button>
      )}
    </>
  )

  const showOwner =
    artifact && artifact.scopeKind === 'unit' && artifact.artifactType === 'narrative_document'
  // The reader is a place to step into once there is something to read —
  // with nothing written it would only show empty headings.
  const hasWrittenContent = sections.some(
    (section) => (sectionStatuses.find((st) => st.sectionKey === section.sectionKey)?.wordCount ?? 0) > 0,
  )
  // The menu holds Save as Template (any non-viewer) or, on an empty outline,
  // From Template — an outline write.
  const hasOverflow = !isFinalized && !isViewOnly && (sections.length > 0 || !isReadOnly)

  return (
    <div className="flex flex-col h-full">
      <ArtifactPageHeader
        title={artifact?.name ?? 'Loading...'}
        description={artifact?.description ?? null}
        onBack={() => void navigate(`/app/bids/${bidId}/write`)}
        owner={
          showOwner ? (
            <ArtifactOwnerSelector
              artifact={artifact}
              bidMembers={bidMembers}
              canSetOwner={isBidOwner && !isReadOnly}
              onSetOwner={(userId) => void handleSetArtifactOwner(userId)}
              compact
            />
          ) : undefined
        }
        titleBadges={
          <ArtifactStatusBadge status={artifact?.status} />
        }
        saveStatus={{ isSaving: isSavingOutline }}
        actions={
          <>
            {artifactId && !isInReview && !isFinalized && (
              <SubmitForReviewButton
                artifactId={artifactId}
                bidMembers={bidMembers}
                currentUserId={currentUserId}
                canSubmit={isBidOwner && !isReadOnly}
                disabledReason={submitDisabledReason}
              />
            )}
            {isFinalized && isOwner && !isReadOnly && (
              <Button variant="ghost" size="sm" onClick={() => setShowReopenDialog(true)}>
                <Unlock className="h-4 w-4 mr-2" />
                Reopen
              </Button>
            )}
            {hasOverflow && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9" aria-label="More actions">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {sections.length > 0 ? (
                    <DropdownMenuItem onClick={() => setShowSaveAsTemplate(true)}>
                      <LayoutTemplate className="h-4 w-4 mr-2" />
                      Save as Template
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => setShowTemplatePicker(true)}>
                      <LayoutTemplate className="h-4 w-4 mr-2" />
                      From Template
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {/* Export is a POST, which the backend refuses a view-only user. */}
            {!isViewOnly && (
            <ExportDropdown
              artifactId={artifactId}
              disabled={isExporting || sections.length === 0}
              isExporting={isExporting}
              onExport={handleExport}
              onSaveToSharePoint={handleSaveToSharePoint}
              isSharePointConnected={isSharePointConnected}
            />
            )}
            {isMultiSectionDocument && !isLocked && sections.length > 0 && (
              draftDocument.isCheckingAvailability
                // Holds the button's place so the header doesn't shift when it arrives.
                ? <span aria-hidden="true" data-testid="draft-button-placeholder" className="inline-block h-8 w-[8.5rem]" />
                : showDraftDocument
                  ? <DraftDocumentButton draftDocument={draftDocument} />
                  : <DraftAllButton draftAll={draftAll} />
            )}
            {!isFinalized && !isReadOnly && (
              <Button
                variant={isDraftDocumentInHeader ? "secondary" : "default"}
                size="sm"
                onClick={() => {
                  const firstSection = sections[0]
                  if (firstSection) {
                    handleStartDrafting(firstSection.sectionKey)
                  }
                }}
                disabled={sections.length === 0}
              >
                <FileEdit className="h-4 w-4 mr-2" />
                Start drafting
              </Button>
            )}
          </>
        }
      />

      {/* Everything under the header scrolls as one page. The banners and the
          job card (its recap can be long) sit above the sections and the
          questions, so a short window still reaches "Questions for this
          document" (UX2 05a). */}
      <div data-testid="document-overview-scroll" className="flex-1 overflow-auto px-6 pt-1 pb-4">
        {/* Direct children only: a banner that renders nothing leaves no gap. */}
        <div className="flex flex-col gap-3 pt-2 pb-1 empty:hidden">
          {/* Review banner (pending / changes_requested / finalized) */}
          {artifact && (
            <ReviewStatusBanner
              artifact={artifact}
              currentUserId={currentUserId}
              isBidOwner={isBidOwner}
            />
          )}

          {(draftAll.run || draftAll.outcome) && (
            <DraftAllNotice draftAll={draftAll} />
          )}

          {showDraftDocument && artifactId && (
            <PanelDocumentJob artifactId={artifactId} overview pendingSuggestions={pendingSuggestionItems} />
          )}

          {waitingSuggestions.length > 0 && !(showDraftDocument && recapListsSuggestions) && (
            <PendingSuggestionsList
              items={waitingSuggestions}
              activeProposalId={null}
              onOpen={(item) => handleStartDrafting(encodeURIComponent(item.sectionId))}
            />
          )}
        </div>

        {/* Table */}
        {sections.length === 0 && !isLoadingOutline ? (
          // Empty state — AI outline proposal owns this area (manual paths
          // beside it). Frozen documents get the message without the CTA: the
          // backend rejects generation and accept on a frozen artifact
          // (spec §2-C1), so offering the button would only yield a raw 400.
          isFrozen ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <ListTree className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <h3 className="text-base font-medium mb-1">No sections yet</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                This document is {isFinalized ? 'finalised' : 'in review'}, so its outline can’t be
                changed.
              </p>
            </div>
          ) : isReadOnly ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <ListTree className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <h3 className="text-base font-medium mb-1">No sections yet</h3>
              <ViewOnlyNotice />
            </div>
          ) : isNarrativeArtifact ? (
            <OutlineProposalArea
              bidId={bidId ?? ''}
              artifactId={artifactId ?? ''}
              artifactType="narrative_document"
              onAccepted={() => {
                if (artifactId) void fetchOutline(artifactId)
              }}
              manualActions={outlineManualActions}
            />
          ) : null
        ) : (
          <>
            {/* Additions mode — the outline is no longer one-shot. Frozen
                documents don't get it (same gate as above).
                ⚠ `!isLoadingOutline` is load-bearing: a cached artifact
                resolves before its outline, so `sections` is briefly [] on a
                document that HAS an outline. Mounting then hands the area zero
                existing sections, which is exactly the first-run "No sections
                yet / Propose outline" hero — stacked over the empty table, and
                indistinguishable from the outline having been wiped. */}
            {!isLocked && !isLoadingOutline && isNarrativeArtifact && (
              // No manual actions here: "Add section" appears once, at the
              // foot of the table. The idle "Propose more sections" trigger
              // sits in the progress row below, so the area renders only
              // notes, the generating strip or the review — each spaced on
              // its own — and nothing at all otherwise.
              <OutlineProposalArea
                bidId={bidId ?? ''}
                artifactId={artifactId ?? ''}
                artifactType="narrative_document"
                existingSections={sections}
                onAccepted={() => {
                  if (artifactId) void fetchOutline(artifactId)
                }}
                manualActions={null}
                additionsTrigger="external"
              />
            )}
            <ProgressSummary
              sections={sections}
              assignments={assignments}
              sectionStatuses={sectionStatuses}
              action={
                <>
                  {/* Same gate as the area's mount above — hidden while a
                      proposal is generating or under review. */}
                  {!isLocked && !isLoadingOutline && isNarrativeArtifact && (
                    <ProposeMoreSectionsButton artifactId={artifactId ?? ''} />
                  )}
                  {hasWrittenContent && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void navigate(`/app/bids/${bidId}/responses/${artifactId}/document`)}
                    >
                      <BookOpen className="h-4 w-4 mr-2" />
                      Full response view
                    </Button>
                  )}
                </>
              }
            />
            <div className="border rounded-lg overflow-x-auto bg-card shadow-sm">
              {/* Fixed layout: every column but Section has a set width and
                  Section takes the rest. Width budget in
                  lib/sections-table-columns.ts: 774px fixed + 248px Section
                  minimum = 1022px, which fits the ~1038px box at a 1280
                  window (no sideways scroll); ~426px for Section at 1440.
                  Updated (72px) only returns from 2xl. */}
              <table className="w-full min-w-[1022px] table-fixed">
              <colgroup>
                {SECTIONS_TABLE_COLUMNS.map((column) => (
                  <col key={column.key} data-column={column.key} className={column.colClassName || undefined} />
                ))}
              </colgroup>
              <thead>
              <tr className="border-b bg-muted/50">
                <th className="py-3" />
                <th className="pl-1 pr-1 py-3 text-left text-sm font-medium text-muted-foreground">
                  #
                </th>
                <th className="px-3 py-3 text-left text-sm font-medium text-muted-foreground">
                  Section
                </th>
                <th className="pl-1.5 pr-1 py-3 text-left text-sm font-medium text-muted-foreground">
                  Status
                </th>
                <th className="px-2 py-3 text-left text-sm font-medium text-muted-foreground">
                  Owner
                </th>
                <th className="px-2 py-3 text-left text-sm font-medium text-muted-foreground">
                  Reviewer
                </th>
                <th className="px-2 py-3 text-left text-sm font-medium text-muted-foreground">
                  Submission
                </th>
                <th className="pl-3 pr-1 py-3 text-left text-sm font-medium text-muted-foreground">
                  Words
                </th>
                <th className="px-3 py-3 text-left text-sm font-medium text-muted-foreground">
                  Brief
                </th>
                <th className={cn(UPDATED_CELL_VISIBILITY, "px-2 py-3 text-left text-sm font-medium text-muted-foreground")}>
                  Updated
                </th>
                <th className="sticky right-0 z-[1] bg-card px-1 py-3">
                  <div aria-hidden="true" className="absolute inset-0 bg-muted/50" />
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sections.map((section, index) => {
                const status = sectionStatuses.find(
                  (s) => s.sectionKey === section.sectionKey
                )
                const assignment = assignments.find(
                  (a) => a.sectionId === section.sectionKey
                )
                return (
                  <SectionRow
                    key={section.sectionKey}
                    section={section}
                    index={index}
                    status={status}
                    collaborationStatus={assignment?.collaborationStatus ?? null}
                    pendingSuggestion={pendingBySection.get(section.sectionKey) ?? null}
                    isEditing={!isLocked && editingSectionKey === section.sectionKey}
                    editValue={editValue}
                    onEditChange={setEditValue}
                    onEditStart={isLocked ? () => {} : () => handleEditStart(section)}
                    onEditSave={handleEditSave}
                    onEditCancel={handleEditCancel}
                    onDelete={isLocked ? () => {} : () => setDeleteTarget(section)}
                    onStartDrafting={() => handleStartDrafting(section.sectionKey)}
                    onStatusChange={isLocked ? () => {} : (status) => void handleStatusChange(section.sectionKey, status)}
                    onUpdateTargetWordCount={(value) => {
                      // A typed target is the team's length, so it records basis
                      // 'user'. Re-confirming the same number changes nothing —
                      // it must not relabel a tender limit as a person's choice.
                      updateSection(
                        section.sectionKey,
                        value !== undefined && value !== section.targetWordCount
                          ? { targetWordCount: value, targetWordCountBasis: 'user' }
                          : {}
                      )
                      autoSaveOutline()
                    }}
                    isFinalized={isLocked}
                    totalSections={sections.length}
                    briefButton={
                      <BriefButton
                        bidId={bidId ?? ''}
                        artifactId={artifactId ?? ''}
                        sectionKey={section.sectionKey}
                        status
                        onOpen={() => {
                          setBriefSection({ sectionKey: section.sectionKey, title: section.title })
                          setBriefOpen(true)
                        }}
                      />
                    }
                    dragIndex={dragIndex}
                    dropTargetIndex={dropTargetIndex}
                    onDragStart={setDragIndex}
                    onDragOver={(e, i) => {
                      if (dragIndex === null || dragIndex === i) return
                      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                      const midY = rect.top + rect.height / 2
                      const target = e.clientY < midY ? i : i + 1
                      const noOp = target === dragIndex || target === dragIndex + 1
                      setDropTargetIndex(noOp ? null : target)
                    }}
                    onDrop={handleDrop}
                    onDragEnd={() => { setDragIndex(null); setDropTargetIndex(null) }}
                    submissionLinks={
                      <SubmissionLinksIndicator
                        bidId={bidId ?? ''}
                        artifactId={artifactId ?? ''}
                        sectionKey={section.sectionKey}
                        links={submissionLinks.filter((link) => link.sectionKey === section.sectionKey)}
                        onLinked={async () => {
                          if (!artifactId) return
                          const data = await getArtifactSubmissionLinks(artifactId)
                          setSubmissionLinks(data.links)
                        }}
                        compact
                      />
                    }
                    ownerCell={
                      <SectionOwnerCell
                        assignment={assignment?.userId ? {
                          userId: assignment.userId,
                          fullName: assignment.fullName ?? bidMembers.find((m) => m.userId === assignment.userId)?.fullName ?? null,
                          avatarUrl: assignment.avatarUrl ?? bidMembers.find((m) => m.userId === assignment.userId)?.avatarUrl ?? null,
                        } : undefined}
                        bidMembers={bidMembers}
                        canAssign={canAssignSections}
                        currentUserId={currentUserId}
                        onAssign={(userId) => {
                          if (!artifactId) return
                          const isSelf = userId === currentUserId
                          const member = bidMembers.find((m) => m.userId === userId)
                          const name = member?.fullName || member?.email || "User"
                          void assignSection(artifactId, section.sectionKey, userId)
                          toast(isSelf ? "Section assigned to you" : `Section assigned to ${name}`, {
                            action: {
                              label: "Undo",
                              onClick: () => void unassignSection(artifactId, section.sectionKey),
                            },
                          })
                        }}
                        onUnassign={() => {
                          if (!artifactId) return
                          void unassignSection(artifactId, section.sectionKey)
                          toast("Section unassigned")
                        }}
                        isFinalized={isLocked}
                        appearance="labelled"
                      />
                    }
                    reviewerCell={
                      <SectionReviewerCell
                        reviewerId={assignment?.reviewerId ?? null}
                        plannedReviewerId={assignment?.plannedReviewerId ?? null}
                        plannedReviewerName={assignment?.plannedReviewerName ?? null}
                        ownerUserId={assignment?.userId ?? null}
                        bidMembers={bidMembers}
                        currentUserId={currentUserId}
                        canPlan={canAssignSections}
                        isFinalized={isLocked}
                        onPlan={(userId) => void handlePlanReviewer(section.sectionKey, userId)}
                      />
                    }
                  />
                )
              })}
              {!isLocked && <AddSectionRow onAdd={handleAddSectionWithEdit} />}
            </tbody>
              </table>
            </div>
          </>
        )}

        {/* The AI's questions about this document (R1 Q2). */}
        {bidId && artifactId && (
          <DocumentQuestionsList bidId={bidId} artifactId={artifactId} sectionHeadings={sectionHeadings} className="mt-6" />
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Section?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove "{deleteTarget?.title}" from the outline. Any drafted
              content for this section will be orphaned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reopen Confirmation */}
      <AlertDialog
        open={showReopenDialog}
        onOpenChange={setShowReopenDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen {artifact?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Sections will become editable again. Team members will be able to
              modify their assigned sections.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleReopen()}
              disabled={isReopening}
            >
              {isReopening && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Reopen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Template dialogs */}
      <TemplateFormDialog
        open={showSaveAsTemplate}
        onOpenChange={setShowSaveAsTemplate}
        {...(artifact?.name ? { initialName: artifact.name } : {})}
        initialSections={sections.map((s) => {
          const section: OutlineTemplateSection = { title: s.title ?? '' }
          if (s.description) section.description = s.description
          if (s.targetWordCount) section.targetWordCount = s.targetWordCount
          return section
        })}
        onSave={handleSaveAsTemplate}
        mode="save"
      />

      <TemplatePickerDialog
        open={showTemplatePicker}
        onOpenChange={setShowTemplatePicker}
        currentUserId={currentUserId ?? ''}
        onSelect={handleApplyTemplate}
      />

      <AlertDialog
        open={showTemplateReplaceConfirm}
        onOpenChange={setShowTemplateReplaceConfirm}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace current outline?</AlertDialogTitle>
            <AlertDialogDescription>
              This will replace your current outline with the template. Your existing sections will be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingTemplateSections(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingTemplateSections) {
                  applyTemplateSections(pendingTemplateSections)
                  setPendingTemplateSections(null)
                  setShowTemplateReplaceConfirm(false)
                }
              }}
            >
              Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* SharePoint export — dialog opens when user picks "Save to SharePoint"
          from the Export dropdown. Mounted only when open so the picker JS
          loads lazily. */}
      {sharePointSaveOpen && artifactId && artifact && (
        <SharePointBrowser
          open={sharePointSaveOpen}
          onClose={() => setSharePointSaveOpen(false)}
          mode="save"
          defaultFilename={`${(artifact.name ?? 'Response').replace(/[^a-zA-Z0-9_\- ]/g, '_').trim()}.docx`.slice(0, 150)}
          exportParams={{
            artifactId,
            templateId: sharePointExportTemplateId,
          }}
        />
      )}

      {briefSection && (
        <BriefModal
          open={briefOpen}
          onOpenChange={setBriefOpen}
          bidId={bidId ?? ''}
          artifactId={artifactId ?? ''}
          sectionKey={briefSection.sectionKey}
          unitTitle={briefSection.title}
          scopeKind="unit"
          canWrite={!isFrozen}
          onOpenEditor={() => {
            setBriefOpen(false)
            void navigate(`/app/bids/${bidId}/responses/${artifactId}/sections/${briefSection.sectionKey}`)
          }}
        />
      )}

      <ExportGapConfirmDialog {...exportGapDialogProps} />
    </div>
  )
}
